import { sendPlaybackEvents } from '../playback/send-playback-events.js';
import { monotonicNowMs, offsetFromFourTimestamps, offsetFromRoundTrip, ServerClock, } from './server-clock.js';
import { DeviceApiError } from '../api/device-api.js';
import { ensurePlaylistCached } from '../cache/ensure-cached.js';
import { runDeviceCommands } from '../commands/runner.js';
import { resolveDesiredPlaylist } from '../schedule/resolve-desired.js';
import { consoleLogger } from '../ports/logger.js';
const DEFAULT_SYNC_INTERVAL_MS = 30_000;
/** The API answers 401 once a device is revoked, or its screen deleted. */
function isCredentialRejected(error) {
    return error instanceof DeviceApiError && (error.status === 401 || error.status === 403);
}
/**
 * Polls `/v1/device/sync`, runs commands, caches schedule playlists, activates desired.
 */
export class DeviceSyncLoop {
    options;
    /** Server time estimate from sync round trips (used to keep wall tiles on the shared timeline). */
    serverClock = new ServerClock();
    /**
     * A quick burst of clock samples on a warm connection, keeping the one with the
     * smallest network delay. Used by video walls at start (snap) and every minute while
     * the wall runs (smoothed). Without /time on the API, sync samples are used instead.
     */
    async sampleServerTime(count, options) {
        const api = this.options.api;
        if (!api.getTime)
            return;
        const credential = await this.options.credentials.get();
        if (!credential)
            return;
        const samples = [];
        for (let index = 0; index < count; index += 1) {
            if (index > 0)
                await new Promise((resolve) => setTimeout(resolve, 200));
            const sentWallMs = Date.now();
            const sentMonoMs = monotonicNowMs();
            try {
                const time = await api.getTime(credential.token);
                const sample = offsetFromFourTimestamps({
                    clientSentMs: sentWallMs,
                    serverReceivedMs: Date.parse(time.serverReceivedAt),
                    serverSentMs: Date.parse(time.serverSentAt),
                    clientReceivedMs: sentWallMs + (monotonicNowMs() - sentMonoMs),
                });
                if (sample)
                    samples.push(sample);
            }
            catch {
                return; // older API without /time: keep the sync samples
            }
        }
        if (samples.length === 0)
            return;
        const kept = samples.reduce((best, next) => (next.rttMs < best.rttMs ? next : best));
        this.serverClock.record(kept, { snap: options.snap });
        const offsets = samples.map((sample) => Math.round(sample.offsetMs));
        this.logger.info('Server clock burst', {
            offsetsMs: offsets,
            networkMs: samples.map((sample) => Math.round(sample.rttMs)),
            spreadMs: Math.max(...offsets) - Math.min(...offsets),
            keptOffsetMs: Math.round(kept.offsetMs),
            keptNetworkMs: Math.round(kept.rttMs),
            usedOffsetMs: Math.round(this.serverClock.offsetMs() ?? 0),
            snap: options.snap,
        });
    }
    timer = null;
    schedule = null;
    logger;
    running = false;
    syncIntervalMs;
    constructor(options) {
        this.options = options;
        this.logger = options.logger ?? consoleLogger;
        this.syncIntervalMs = options.syncIntervalMs ?? DEFAULT_SYNC_INTERVAL_MS;
    }
    getSchedule() {
        return this.schedule;
    }
    /** Cold-start: restore the last sync schedule so dayparts work before the next poll. */
    restoreSchedule(schedule) {
        this.schedule = schedule;
    }
    start() {
        if (this.timer)
            return;
        void this.syncOnce();
        this.armTimer();
    }
    stop() {
        if (this.timer)
            clearInterval(this.timer);
        this.timer = null;
    }
    armTimer() {
        if (this.timer)
            clearInterval(this.timer);
        this.timer = setInterval(() => {
            void this.syncOnce();
        }, this.syncIntervalMs);
    }
    applySyncInterval(ms) {
        if (!Number.isFinite(ms) || ms < 5_000)
            return;
        if (ms === this.syncIntervalMs)
            return;
        this.syncIntervalMs = ms;
        if (this.timer)
            this.armTimer();
    }
    async syncOnce() {
        if (this.running)
            return;
        this.running = true;
        try {
            await this.runSync();
            await this.sendPlaybackEvents();
        }
        catch (error) {
            if (isCredentialRejected(error)) {
                await this.handleCredentialRejected();
            }
            else {
                this.logger.warn('Sync cycle failed', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }
        finally {
            this.running = false;
        }
    }
    /** Offline daypart tick between polls. */
    async evaluateScheduleLocally(now = new Date()) {
        try {
            return await this.runScheduleTick(now);
        }
        catch (error) {
            if (isCredentialRejected(error)) {
                await this.handleCredentialRejected();
                return null;
            }
            this.logger.warn('Schedule tick failed', {
                error: error instanceof Error ? error.message : String(error),
            });
            return null;
        }
    }
    /**
     * The operator disconnected the player or deleted its screen. Drop the local
     * pairing and cached content so the shell returns to the pairing code prompt
     * instead of sitting on content it may no longer be entitled to show.
     */
    async handleCredentialRejected() {
        this.stop();
        this.schedule = null;
        this.options.cache.clear();
        await this.options.assets.clear();
        await this.options.offlineState?.clear();
        this.options.playbackEvents?.clear();
        await this.options.credentials.clear();
        this.logger.warn('Device credential rejected — returning to pairing');
        await this.options.onRevoked?.();
    }
    async sendPlaybackEvents() {
        const buffer = this.options.playbackEvents;
        // Also send when only a queue-loss report is waiting (e.g. the last batch was set aside).
        if (!buffer || (buffer.size() === 0 && !buffer.pendingQueueLoss()))
            return;
        const credential = await this.options.credentials.get();
        if (!credential)
            return;
        await sendPlaybackEvents({
            api: this.options.api,
            token: credential.token,
            buffer,
            logger: this.logger,
        });
    }
    async persistOfflineState() {
        const store = this.options.offlineState;
        if (!store)
            return;
        await store.savePlaylistCache(this.options.cache.toSnapshot());
        await store.saveSchedule(this.schedule);
    }
    async runScheduleTick(now) {
        if (!this.schedule)
            return null;
        const desired = resolveDesiredPlaylist(this.schedule, now);
        const credential = await this.options.credentials.get();
        if (!credential)
            return null;
        const cached = await ensurePlaylistCached({
            api: this.options.api,
            token: credential.token,
            ref: { playlistId: desired.playlistId, version: desired.playlistVersion },
            cache: this.options.cache,
            assets: this.options.assets,
            hasher: this.options.hasher,
            logger: this.logger,
        });
        if (!cached)
            return null;
        const activated = this.options.cache.activate(cached.ref);
        if (activated && this.options.onActivated) {
            await this.persistOfflineState();
            await this.options.onActivated(activated.ref);
        }
        return activated?.ref ?? null;
    }
    async runSync() {
        const credential = await this.options.credentials.get();
        if (!credential) {
            this.logger.debug('Sync skipped — not paired');
            return;
        }
        const current = this.options.cache.getCurrent();
        const syncSentAtMs = monotonicNowMs();
        const syncSentWallMs = Date.now();
        const response = await this.options.api.sync(credential.token, {
            deviceId: credential.deviceId,
            playlistVersion: current?.ref.version ?? 0,
            appVersion: this.options.appVersion,
            platform: this.options.identity.platform,
            playerBuild: this.options.identity.playerBuild,
            capabilities: this.options.identity.capabilities,
            ...(this.options.identity.manufacturer
                ? { manufacturer: this.options.identity.manufacturer }
                : {}),
            ...(this.options.identity.model ? { model: this.options.identity.model } : {}),
        });
        const syncRttMs = monotonicNowMs() - syncSentAtMs;
        const clockSample = response.serverReceivedAt && response.serverSentAt
            ? offsetFromFourTimestamps({
                clientSentMs: syncSentWallMs,
                serverReceivedMs: Date.parse(response.serverReceivedAt),
                serverSentMs: Date.parse(response.serverSentAt),
                clientReceivedMs: syncSentWallMs + syncRttMs,
            })
            : offsetFromRoundTrip({
                serverTimeMs: Date.parse(response.serverTime),
                receivedAtMs: Date.now(),
                rttMs: syncRttMs,
            });
        this.serverClock.record(clockSample);
        if (clockSample) {
            this.logger.info('Server clock sample', {
                offsetMs: Math.round(clockSample.offsetMs),
                rttMs: Math.round(clockSample.rttMs),
                usedOffsetMs: Math.round(this.serverClock.offsetMs() ?? 0),
            });
        }
        await this.options.onScaleMode?.(response.scaleMode ?? 'fit');
        await this.options.onRotationDegrees?.(response.rotationDegrees ?? 0);
        this.applySyncInterval(response.syncIntervalMs ?? this.syncIntervalMs);
        await this.options.onIdleDisplay?.(response.idleDisplay ?? 'logo');
        const caps = new Set(this.options.identity.capabilities);
        const commandResults = await runDeviceCommands(response.commands, {
            ...(caps.has('cmd_sync_now') ? { syncNow: async () => undefined } : {}),
            ...(caps.has('cmd_clear_cache')
                ? {
                    clearCache: async () => {
                        await this.options.assets.clear();
                        this.options.cache.clear();
                        this.schedule = null;
                        await this.options.offlineState?.clear();
                    },
                }
                : {}),
            ...(caps.has('cmd_restart_player') && this.options.restartPlayer
                ? { restartPlayer: this.options.restartPlayer }
                : {}),
        });
        for (const entry of commandResults) {
            const ack = entry.result.message !== undefined
                ? {
                    commandId: entry.commandId,
                    status: entry.result.status,
                    message: entry.result.message,
                }
                : {
                    commandId: entry.commandId,
                    status: entry.result.status,
                };
            await this.options.api.ackCommand(credential.token, ack);
        }
        await this.options.onWallAssignment?.(response.wallAssignment ?? null, {
            serverTime: response.serverTime,
            serverNow: () => this.serverClock.now(),
        });
        // Wall tile media takes precedence over playlist while ready (ADR 0007).
        if (response.wallAssignment?.item) {
            return;
        }
        if (response.schedule) {
            this.schedule = response.schedule;
            for (const window of response.schedule.windows) {
                await ensurePlaylistCached({
                    api: this.options.api,
                    token: credential.token,
                    ref: { playlistId: window.playlistId, version: window.playlistVersion },
                    cache: this.options.cache,
                    assets: this.options.assets,
                    hasher: this.options.hasher,
                    logger: this.logger,
                });
            }
            await ensurePlaylistCached({
                api: this.options.api,
                token: credential.token,
                ref: {
                    playlistId: response.schedule.defaultPlaylistId,
                    version: response.schedule.defaultPlaylistVersion,
                },
                cache: this.options.cache,
                assets: this.options.assets,
                hasher: this.options.hasher,
                logger: this.logger,
            });
        }
        if (response.desiredPlaylistId && response.desiredPlaylistVersion > 0) {
            const ref = {
                playlistId: response.desiredPlaylistId,
                version: response.desiredPlaylistVersion,
            };
            const cached = await ensurePlaylistCached({
                api: this.options.api,
                token: credential.token,
                ref,
                cache: this.options.cache,
                assets: this.options.assets,
                hasher: this.options.hasher,
                logger: this.logger,
            });
            if (cached) {
                const activated = this.options.cache.activate(ref);
                if (activated && this.options.onActivated) {
                    await this.persistOfflineState();
                    await this.options.onActivated(activated.ref);
                }
                else {
                    await this.persistOfflineState();
                }
            }
            else {
                this.logger.warn('Desired playlist failed integrity — keeping previous', {
                    playlistId: ref.playlistId,
                    version: ref.version,
                });
                this.options.cache.rollback();
                await this.persistOfflineState();
            }
        }
        else {
            await this.persistOfflineState();
        }
    }
}
//# sourceMappingURL=sync-loop.js.map